import React, { useMemo } from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, ReferenceLine, ReferenceArea,
  ReferenceDot, Tooltip, ResponsiveContainer,
} from 'recharts';
import { PositionBoundaries, expiryCloseMs, ruleLinesMeet, curveUntil, curveValueAt } from '../../../utils/positionBoundaries';

/**
 * Position ticket price chart (Oct 2026): a trading-terminal read of the
 * position, not a sparkline. Right price axis with tagged levels (last,
 * strike, break-even, entry), a date axis, faint horizontal grid, the
 * break-even → strike band, entry / expiry / time-stop markers, and the
 * user's rule curves (profit target, defensive delta) projected to expiry.
 * Colours are the trader tokens: green above break-even, red below.
 */

interface Props {
  bars: Array<{ close: number; timestamp: number }> | undefined;
  height?: number;
  isPut: boolean;
  strike: number;
  breakEven: number | null;     // CC: net basis · CSP: strike − premium
  entryPrice?: number | null;   // CC stock entry (dimmer level)
  entryDate?: string | null;
  expiryDate: string;
  lastPrice?: number | null;
  boundaries?: PositionBoundaries | null;
  premium?: number | null;      // credit per share, shown on the entry marker
  earningsDate?: string | null;    // marked when it falls before expiry
}

const C = {
  gain: '#5cc79a', loss: '#e08a7e', warn: '#e8b85a', sig: '#7fd0ea',
  t1: '#e7e8ea', t2: '#a2a7ae', t3: '#6f747c', line: 'rgba(255,255,255,0.06)', bg: '#16171b',
};
const MONO = "'JetBrains Mono Variable', 'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace";
const DAY = 86_400_000;
const ts = (iso: string) => expiryCloseMs(iso);  // 4 pm New York: same clock as the curves
const fmtDate = (t: number) => new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const fmtPx = (v: number) => (v >= 1000 ? v.toFixed(0) : v >= 100 ? v.toFixed(1) : v.toFixed(2));

/** Price tag drawn on the right axis, TradingView-style. */
const Tag: React.FC<any> = ({ viewBox, text, color, filled }) => {
  if (!viewBox) return null;
  const x = viewBox.x + viewBox.width + 1;
  const y = viewBox.y;
  const w = Math.max(44, text.length * 6.2 + 8);
  return (
    <g>
      <rect x={x} y={y - 8} width={w} height={16} rx={2}
        fill={filled ? color : C.bg} stroke={color} strokeWidth={1} />
      <text x={x + 4} y={y + 3.5} fontFamily={MONO} fontSize={10}
        fill={filled ? '#0b0c0e' : color}>{text}</text>
    </g>
  );
};

/** Small label at the top of a vertical marker. */
const TopLabel: React.FC<any> = ({ viewBox, text, color, left }) => {
  if (!viewBox) return null;
  return (
    <text x={viewBox.x + (left ? -4 : 4)} y={viewBox.y + 10} textAnchor={left ? 'end' : 'start'}
      fontFamily={MONO} fontSize={9.5} fill={color} letterSpacing="0.04em">{text}</text>
  );
};

/** Entry: hollow ring on the price line + "SOLD 6.70" callout. */
const EntryMark: React.FC<any> = ({ cx, cy, text }) => {
  if (cx == null || cy == null) return null;
  const w = text.length * 5.8 + 8;
  return (
    <g>
      <circle cx={cx} cy={cy} r={4} fill={C.bg} stroke={C.t1} strokeWidth={1.5} />
      <rect x={cx - w / 2} y={cy + 8} width={w} height={14} rx={2} fill={C.bg} stroke={C.t3} strokeWidth={1} />
      <text x={cx} y={cy + 18} textAnchor="middle" fontFamily={MONO} fontSize={9.5} fill={C.t2}>{text}</text>
    </g>
  );
};

/** Expiry: diamond where the expiry line meets the strike — where the trade resolves. */
const ExpiryMark: React.FC<any> = ({ cx, cy, color }) => {
  if (cx == null || cy == null) return null;
  const r = 5;
  return (
    <path d={`M ${cx} ${cy - r} L ${cx + r} ${cy} L ${cx} ${cy + r} L ${cx - r} ${cy} Z`}
      fill={C.bg} stroke={color} strokeWidth={1.5} />
  );
};

/** Where the rule lines meet: from here every price fires a rule. */
const ActMark: React.FC<any> = ({ cx, cy, text }) => {
  if (cx == null || cy == null) return null;
  const w = text.length * 5.8 + 8;
  return (
    <g>
      <circle cx={cx} cy={cy} r={3.5} fill={C.bg} stroke={C.t1} strokeWidth={1.5} />
      <rect x={cx - w / 2} y={cy - 24} width={w} height={14} rx={2} fill={C.bg} stroke={C.t2} strokeWidth={1} />
      <text x={cx} y={cy - 14} textAnchor="middle" fontFamily={MONO} fontSize={9.5} fill={C.t1}>{text}</text>
    </g>
  );
};

/** Round ticks (1/2/2.5/5 × 10^n), minus any that would sit under a level tag. */
function niceTicks(lo: number, hi: number, tags: number[]): number[] {
  const raw = (hi - lo) / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((v) => v >= raw) ?? 10 * mag;
  const out: number[] = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi; v += step) out.push(+v.toFixed(6));
  const gap = (hi - lo) * 0.06;
  return out.filter((v) => tags.every((t) => Math.abs(v - t) > gap));
}

const PositionPriceChart: React.FC<Props> = ({
  bars, height = 224, isPut, strike, breakEven, entryPrice, entryDate, expiryDate, lastPrice, boundaries, premium,
  earningsDate,
}) => {
  const model = useMemo(() => {
    const src = (bars ?? []).filter((b) => b && Number.isFinite(b.close) && Number.isFinite(b.timestamp))
      .sort((a, b) => a.timestamp - b.timestamp);
    if (src.length < 2) return null;
    const entryTs = entryDate ? ts(entryDate) : null;
    const expiryTs = ts(expiryDate);
    // Context: two weeks before entry (or the whole series if entry is unknown).
    const start = entryTs ? Math.max(src[0].timestamp, entryTs - 14 * DAY) : src[0].timestamp;
    const pts = src.filter((b) => b.timestamp >= start).map((b) => ({ t: b.timestamp, px: b.close }));
    const now = Date.now();
    if (lastPrice && lastPrice > 0 && now > pts[pts.length - 1].t + DAY / 2) pts.push({ t: now, px: lastPrice });
    const last = pts[pts.length - 1];

    const curve = (c: Array<{ timestamp: number; stockPrice: number }> | null | undefined) =>
      (c ?? []).filter((p) => p.timestamp >= last.t && p.timestamp <= expiryTs).map((p) => ({ t: p.timestamp, v: p.stockPrice }));
    // The engine's lines (server, through expiry: the delta line ends at the
    // strike, the profit line at strike ± the target price). Drawn as served.
    let pt = curve(boundaries?.profitTargetCurve);
    let db = curve(boundaries?.deltaBreachCurve);
    let ptEnd: number | null = pt.length > 1 && pt[pt.length - 1].t === expiryTs ? pt[pt.length - 1].v : null;
    // Where the profit line meets the defend boundary, the "hold" band closes:
    // from then on every price fires a rule, so the lines stop there ("ACT").
    // Keep My Shares: the defend boundary is the lower of the delta line and
    // the strike-proximity line (whichever fires first as the stock rises).
    const prox = boundaries?.strikeProximityPrice ?? null;
    const defend = prox == null ? db
      : db.length > 1 ? db.map((p) => ({ t: p.t, v: Math.min(p.v, prox) }))
      : pt.map((p) => ({ t: p.t, v: prox }));
    let meet = pt.length > 1 && defend.length > 1 ? ruleLinesMeet(pt, defend, isPut) : null;
    // No hold band at all (they already meet at the first sample): act now —
    // mark today's price rather than a point on a line that isn't drawn.
    const actNow = meet != null && meet.t <= pt[0].t;
    if (meet) {
      pt = curveUntil(pt, meet);
      // Only the lines that meet stop at ACT. When strike proximity governs (the
      // meet sits on the PROX line, below the delta line), the delta line keeps
      // running to the strike at expiry; cutting it left it hanging mid-air.
      const dbEnd = curveValueAt(db, meet.t);
      const deltaMeets = dbEnd != null && (prox == null || (isPut ? dbEnd >= prox : dbEnd <= prox));
      if (deltaMeets) db = curveUntil(db, { t: meet.t, v: dbEnd as number });
      ptEnd = null;  // the profit line no longer reaches expiry
    }
    if (meet && actNow) meet = { t: last.t, v: last.px };
    // Today's price already outside the hold band (past a defend line, or past
    // the profit line): a rule has fired now, whatever the lines do later.
    const ptNow = pt[0]?.v, defendNow = defend[0]?.v;
    const firedNow = (defendNow != null && (isPut ? last.px <= defendNow : last.px >= defendNow))
      || (ptNow != null && (isPut ? last.px >= ptNow : last.px <= ptNow));
    const actNowAll = actNow || (firedNow && (pt.length > 1 || defend.length > 1));
    if (actNowAll && !actNow) {
      meet = { t: last.t, v: last.px };
      pt = [];        // the profit line no longer matters: act now
      ptEnd = null;   // the delta line keeps its course to the strike, for context
    }
    const stop = boundaries?.stockStopPrice ?? null;

    const levels = [strike, breakEven, entryPrice, stop, prox].filter((v): v is number => v != null && Number.isFinite(v));
    // Rule curves widen the scale when they stay near the price (within 12% of
    // the last close); far excursions are clipped, never flattened — clamping
    // made the two curves look like they met before expiry.
    const near = (v: number) => Math.abs(v - last.px) / last.px <= 0.12;
    const curveVals = [...pt, ...db].map((p) => p.v).filter(near);
    const values = [...pts.map((p) => p.px), ...levels, ...curveVals];
    let lo = Math.min(...values), hi = Math.max(...values);
    const pad = Math.max((hi - lo) * 0.08, hi * 0.004);
    lo -= pad; hi += pad;

    // Stroke/fill split at break-even: green above, red below.
    const be = breakEven ?? strike;
    const split = Math.min(1, Math.max(0, (hi - be) / (hi - lo)));

    const series: Array<{ t: number; px?: number; pt?: number; db?: number }> = pts.map((p) => ({ t: p.t, px: p.px }));
    pt.forEach((p) => series.push({ t: p.t, pt: p.v }));
    db.forEach((p) => series.push({ t: p.t, db: p.v }));
    series.sort((a, b) => a.t - b.t);

    const ticks = niceTicks(lo, hi, [...levels, last.px, ...(ptEnd != null ? [ptEnd] : [])]);
    // Entry marker sits on the price line at the bar nearest the entry date.
    const entryPt = entryTs != null
      ? pts.reduce((best, p) => (Math.abs(p.t - entryTs) < Math.abs(best.t - entryTs) ? p : best), pts[0])
      : null;

    return {
      ticks, entryPt, series, last, entryTs, expiryTs, start: pts[0].t, end: expiryTs + 2 * DAY, lo, hi, split, stop,
      hasPt: pt.length > 1, hasDb: db.length > 1, timeStop: boundaries?.timeStopTimestamp ?? null, ptEnd, meet, actNow: actNowAll, prox,
      earningsTs: earningsDate && ts(earningsDate) > (entryTs ?? src[0].timestamp) && ts(earningsDate) < expiryTs ? ts(earningsDate) : null,
      lastColor: last.px >= be ? C.gain : C.loss,
    };
  }, [bars, entryDate, expiryDate, lastPrice, boundaries, strike, breakEven, entryPrice, isPut, earningsDate]);

  if (!model) {
    return <div className="ppc-empty" style={{ height }}>Price history unavailable</div>;
  }

  const { series, last, entryTs, expiryTs, start, end, lo, hi, split, stop } = model;
  const zoneLo = isPut ? strike : (breakEven ?? strike);
  const zoneHi = isPut ? hi : strike;
  // Proximity and break-even can land on the same cent (premium = 2% of the strike): one tag.
  const proxOnBe = model.prox != null && breakEven != null && Math.abs(model.prox - breakEven) / breakEven < 0.002;
  const showEntryLevel = !isPut && entryPrice != null && breakEven != null && Math.abs(entryPrice - breakEven) / entryPrice > 0.002;

  return (
    <div className="ppc" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={series} margin={{ top: 14, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="ppc-stroke" x1="0" y1="0" x2="0" y2="1">
              <stop offset={split} stopColor={C.gain} />
              <stop offset={split} stopColor={C.loss} />
            </linearGradient>
            <linearGradient id="ppc-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset={0} stopColor={C.gain} stopOpacity={0.10} />
              <stop offset={split} stopColor={C.gain} stopOpacity={0.02} />
              <stop offset={split} stopColor={C.loss} stopOpacity={0.02} />
              <stop offset={1} stopColor={C.loss} stopOpacity={0.10} />
            </linearGradient>
          </defs>

          <CartesianGrid vertical={false} stroke={C.line} />
          <XAxis
            dataKey="t" type="number" scale="time" domain={[start, end]}
            tickFormatter={fmtDate} tickCount={6} minTickGap={36}
            tick={{ fill: C.t3, fontSize: 10, fontFamily: MONO }} tickLine={false}
            axisLine={{ stroke: C.line }} height={18}
          />
          <YAxis
            orientation="right" domain={[lo, hi]} ticks={model.ticks} width={54}
            tickFormatter={fmtPx} tick={{ fill: C.t3, fontSize: 10, fontFamily: MONO }}
            axisLine={false} tickLine={false} allowDataOverflow
          />

          {/* Remaining life of the option: today → expiry */}
          <ReferenceArea x1={last.t} x2={expiryTs} fill="rgba(255,255,255,0.025)" stroke="none" ifOverflow="hidden" />
          {/* Break-even → strike band (CC) / above-strike keep-premium band (CSP) */}
          <ReferenceArea y1={zoneLo} y2={zoneHi} fill={C.gain} fillOpacity={0.06} stroke="none" ifOverflow="hidden" />

          {entryTs != null && entryTs >= start && (
            <ReferenceLine x={entryTs} stroke={C.t3} strokeDasharray="2 3" strokeOpacity={0.6} label={<TopLabel text="ENTRY" color={C.t3} />} />
          )}
          {model.timeStop != null && model.timeStop > start && (
            <ReferenceLine x={model.timeStop} stroke={C.warn} strokeDasharray="3 3" label={<TopLabel text="TIME STOP" color={C.warn} />} />
          )}
          <ReferenceLine x={expiryTs} stroke={C.t2} strokeDasharray="4 3" label={<TopLabel text={`EXP ${fmtDate(expiryTs).toUpperCase()}`} color={C.t2} left />} />

          {/* Levels, tagged on the price axis */}
          <ReferenceLine y={strike} stroke={C.gain} strokeOpacity={0.7}
            label={<Tag text={`K ${fmtPx(strike)}`} color={C.gain} />} />
          {breakEven != null && (
            <ReferenceLine y={breakEven} stroke={C.t2} strokeDasharray="4 3" strokeOpacity={0.7}
              label={<Tag text={`BE ${fmtPx(breakEven)}`} color={C.t2} />} />
          )}
          {model.prox != null && (
            // Keep My Shares: at or above this price the stock is within 2% of the strike.
            // Labelled inside the plot (left): an axis tag would not fit the 54px axis.
            <ReferenceLine y={model.prox} stroke={C.warn} strokeDasharray="1 3" strokeOpacity={0.9}
              label={{ value: proxOnBe ? `PROX ≈ BE ${fmtPx(model.prox)}` : `PROX ${fmtPx(model.prox)}`,
                position: 'insideTopLeft', fill: C.warn, fontSize: 9.5, fontFamily: MONO }} />
          )}
          {model.earningsTs != null && (
            <ReferenceLine x={model.earningsTs} stroke={C.warn} strokeDasharray="2 2"
              label={<TopLabel text={`EARN ${fmtDate(model.earningsTs).toUpperCase()}`} color={C.warn} />} />
          )}
          {showEntryLevel && (
            <ReferenceLine y={entryPrice as number} stroke={C.t3} strokeDasharray="1 3"
              label={<Tag text={`IN ${fmtPx(entryPrice as number)}`} color={C.t3} />} />
          )}
          {model.ptEnd != null && (
            // Where the profit-target line lands at expiry, tagged on the axis.
            <ReferenceLine y={model.ptEnd} stroke="none" ifOverflow="hidden"
              label={<Tag text={`PT ${fmtPx(model.ptEnd)}`} color={C.gain} />} />
          )}
          {stop != null && (
            <ReferenceLine y={stop} stroke={C.loss} strokeDasharray="4 3"
              label={<Tag text={`STOP ${fmtPx(stop)}`} color={C.loss} />} />
          )}

          <Area dataKey="px" type="linear" stroke="url(#ppc-stroke)" strokeWidth={1.5}
            fill="url(#ppc-fill)" connectNulls isAnimationActive={false} dot={false} activeDot={{ r: 3, fill: C.t1, stroke: 'none' }} />
          {model.hasPt && (
            <Line dataKey="pt" type="monotone" stroke={C.gain} strokeOpacity={0.8} strokeDasharray="3 3"
              strokeWidth={1} dot={false} connectNulls isAnimationActive={false} activeDot={false} />
          )}
          {model.hasDb && (
            <Line dataKey="db" type="monotone" stroke={C.warn} strokeOpacity={0.8} strokeDasharray="3 3"
              strokeWidth={1} dot={false} connectNulls isAnimationActive={false} activeDot={false} />
          )}

          {/* Last price: dot + filled tag */}
          <ReferenceDot x={last.t} y={last.px} r={2.5} fill={model.lastColor} stroke="none" />
          <ReferenceLine y={last.px} stroke={model.lastColor} strokeOpacity={0.35} strokeDasharray="1 2"
            label={<Tag text={fmtPx(last.px)} color={model.lastColor} filled />} />

          {/* Entry and expiry markers */}
          {model.entryPt && model.entryPt.t >= start && (
            <ReferenceDot x={model.entryPt.t} y={model.entryPt.px} ifOverflow="visible"
              shape={(props: any) => <EntryMark {...props} text={premium != null ? `SOLD ${premium.toFixed(2)}` : 'ENTRY'} />} />
          )}
          <ReferenceDot x={expiryTs} y={strike} ifOverflow="visible"
            shape={(props: any) => <ExpiryMark {...props} color={C.gain} />} />
          {model.meet && (
            <ReferenceDot x={model.meet.t} y={model.meet.v} ifOverflow="visible"
              shape={(props: any) => <ActMark {...props} text={model.actNow ? 'ACT · NOW' : `ACT · ${fmtDate(model.meet!.t).toUpperCase()}`} />} />
          )}

          <Tooltip
            cursor={{ stroke: C.t3, strokeDasharray: '2 2' }}
            isAnimationActive={false}
            content={({ active, payload, label }: any) => {
              if (!active || !payload?.length) return null;
              const row = payload[0]?.payload ?? {};
              if (row.px == null) return null;
              const gap = isPut ? (row.px - strike) / row.px * 100 : (strike - row.px) / row.px * 100;
              return (
                <div className="ppc-tip">
                  <span>{fmtDate(label)}</span>
                  <b>{row.px.toFixed(2)}</b>
                  <span className={gap >= 0 ? '' : 'is-itm'}>{gap >= 0 ? `${gap.toFixed(1)}% OTM` : `${Math.abs(gap).toFixed(1)}% ITM`}</span>
                </div>
              );
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PositionPriceChart;
